import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { formatDateOnly, isDateOnlyTodayOrFuture } from '../../lib/dateOnly';

interface RegistrationSuccessState {
    registrationId?: string;
    programTitle?: string;
    firstName?: string;
    lastName?: string;
    courseNames?: string[];
    totalPrice?: number;
    earlyBirdPrice?: number;
    discountDeadline?: Date;
    managementFee?: number;
    courses?: {
        name: string;
        nameEn: string;
        price: number;
    }[];
}

const RegistrationSuccess: React.FC = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const [loading, setLoading] = useState(false);
    const [registrationData, setRegistrationData] = useState<RegistrationSuccessState | null>(null);

    // 从URL中获取registrationId参数
    const registrationIdFromUrl = searchParams.get('registrationId');

    useEffect(() => {
        // 如果有URL参数，从数据库获取数据
        if (registrationIdFromUrl) {
            setLoading(true);
            const fetchRegistrationData = async () => {
                try {
                    const docRef = doc(db, 'registrations', registrationIdFromUrl);
                    const docSnap = await getDoc(docRef);

                    if (docSnap.exists()) {
                        const data = docSnap.data();

                        // 获取项目信息
                        let programData = null;
                        if (data.program) {
                            const programRef = doc(db, data.program.path);
                            const programSnap = await getDoc(programRef);
                            if (programSnap.exists()) {
                                programData = programSnap.data();
                            }
                        }

                        // 获取课程信息
                        const currentLanguage = i18n.language === 'zh' ? 'zh' : 'en';
                        let coursesData = [];

                        if (data.courses && data.courses.length > 0) {
                            const coursePromises = data.courses.map(async (courseRef) => {
                                try {
                                    const segments = courseRef.path.split('/');
                                    const collectionId = segments[0];
                                    const docId = courseRef.id;

                                    const courseDoc = await getDoc(doc(db, collectionId, docId));

                                    if (courseDoc.exists()) {
                                        const courseData = courseDoc.data();
                                        return {
                                            name: courseData.name || '',
                                            nameEn: courseData.nameEn || '',
                                            price: courseData.price || 0
                                        };
                                    }
                                    return null;
                                } catch (err) {
                                    console.error('获取课程信息失败:', err, courseRef);
                                    return null;
                                }
                            });

                            coursesData = (await Promise.all(coursePromises)).filter(Boolean);
                        }

                        setRegistrationData({
                            registrationId: registrationIdFromUrl,
                            programTitle: programData?.subtitle || data.programTitle || 'Unknown Program',
                            firstName: data?.firstName || '',
                            lastName: data?.lastName || '',
                            courseNames: coursesData.map(course =>
                                currentLanguage === 'zh' ? course.name : course.nameEn
                            ),
                            courses: coursesData,
                            totalPrice: data.totalPrice || 0,
                            earlyBirdPrice: data.earlyBirdPrice || 0,
                            discountDeadline: programData?.discountDeadline
                                ? new Date(programData.discountDeadline.seconds * 1000)
                                : undefined,
                            managementFee: programData?.managementFee || 50
                        });
                    } else {
                        console.error('Registration document not found');
                    }
                } catch (error) {
                    console.error('Error fetching registration:', error);
                } finally {
                    setLoading(false);
                }
            };

            fetchRegistrationData();
        }
    }, [registrationIdFromUrl, i18n.language]);

    // 如果没有registrationId参数，可能是用户直接访问了此页面
    if (!registrationData && !loading && !registrationIdFromUrl) {
        return (
            <div className="container mx-auto px-4 py-16 mt-[160px]">
                <div className="max-w-3xl mx-auto text-center">
                    <div className="mb-6">
                        <svg className="mx-auto h-16 w-16 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                    </div>
                    <h1 className="text-2xl font-bold mb-4">{t('registrationSuccess.invalidAccess')}</h1>
                    <p className="text-gray-600 mb-8">{t('registrationSuccess.invalidAccessDesc')}</p>
                    <Link
                        to="/programs"
                        className="inline-block bg-blue-600 hover:bg-blue-700 text-white py-2 px-6 rounded-md transition-colors"
                    >
                        {t('registrationSuccess.browsePrograms')}
                    </Link>
                </div>
            </div>
        );
    }

    // 加载中状态
    if (loading) {
        return (
            <div className="container mx-auto px-4 py-16 mt-[160px]">
                <div className="max-w-3xl mx-auto text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-600 mx-auto mb-6"></div>
                    <p className="text-gray-600">{t('common.loading')}</p>
                </div>
            </div>
        );
    }

    // 展示报名成功信息
    return (
        <div className="container mx-auto px-4 py-16 mt-[160px]">
            <div className="max-w-3xl mx-auto">
                <div className="text-center mb-10">
                    <div className="inline-flex items-center justify-center h-24 w-24 rounded-full bg-green-100 mb-6">
                        <svg className="h-12 w-12 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                    </div>
                    <h1 className="text-3xl font-bold mb-4">{t('registrationSuccess.title')}</h1>
                    <p className="text-xl text-gray-600">{t('registrationSuccess.subtitle')}</p>
                </div>

                <div className="bg-white shadow-md rounded-lg overflow-hidden mb-8">
                    <div className="p-6">
                        <h2 className="text-xl font-semibold mb-4">{t('registrationSuccess.registrationDetails')}</h2>

                        <div className="space-y-4">
                            <div>
                                <p className="text-sm text-gray-500">{t('registrationSuccess.registrationId')}</p>
                                <p className="font-medium">{registrationData?.registrationId}</p>
                            </div>

                            <div>
                                <p className="text-sm text-gray-500">{t('registrationSuccess.programTitle')}</p>
                                <p className="font-medium">{registrationData?.programTitle}</p>
                            </div>

                            <div>
                                <p className="text-sm text-gray-500">{t('registrationSuccess.studentName')}</p>
                                <p className="font-medium">{registrationData?.firstName} {registrationData?.lastName}</p>
                            </div>

                            <div>
                                <p className="text-sm text-gray-500">{t('registrationSuccess.courses')}</p>
                                {registrationData?.courseNames && registrationData.courseNames.length > 0 ? (
                                    <div className="font-medium">
                                        {registrationData.courseNames.map((courseName, index) => (
                                            <div key={index} className="mb-1">
                                                • {courseName}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="font-medium">{t('registrationSuccess.noCourseSelected')}</p>
                                )}
                            </div>

                            <div>
                                <p className="text-sm text-gray-500">{t('registrationSuccess.status')}</p>
                                <p className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                    {t('registrationSuccess.pending')}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 课程价格信息 */}
                <div className="bg-white shadow-md rounded-lg overflow-hidden mb-8">
                    <div className="p-6">
                        <h2 className="text-xl font-semibold mb-4">{t('registrationSuccess.priceDetails')}</h2>

                        <div className="space-y-4">
                            {registrationData?.discountDeadline && isDateOnlyTodayOrFuture(registrationData.discountDeadline) && (
                                <div className="bg-blue-50 p-4 rounded-lg">
                                    <div className="flex justify-between items-center mb-2">
                                        <span className="text-2xl font-bold text-blue-800">
                                            ${registrationData.earlyBirdPrice}
                                        </span>
                                    </div>
                                    <p className="text-lg text-blue-600">
                                        {t('registrationSuccess.ifPaidBefore', {
                                            date: formatDateOnly(registrationData.discountDeadline)
                                        })}
                                    </p>
                                </div>
                            )}

                            <div className="bg-gray-50 p-4 rounded-lg">
                                <div className="flex justify-between items-center mb-2">
                                    <span className="text-2xl font-bold text-gray-800">
                                        ${registrationData?.totalPrice || 0}
                                    </span>
                                </div>
                                <p className="text-lg text-gray-600">
                                    {t('registrationSuccess.ifPaidAfter', {
                                        date: formatDateOnly(registrationData?.discountDeadline)
                                    })}
                                </p>
                                {/* <div className="mt-2 text-sm text-gray-500">
                                    <span className="font-medium">{t('registrationSuccess.includingManagementFee')}</span>
                                    <span> (${registrationData?.managementFee || 0})</span>
                                </div> */}
                            </div>
                        </div>
                    </div>
                </div>

                {/* 付款信息 */}
                <div className="bg-white shadow-md rounded-lg overflow-hidden mb-8">
                    <div className="p-6">
                        <h2 className="text-xl font-semibold mb-4">{t('registrationSuccess.paymentOptions')}</h2>

                        <div className="space-y-6">
                            {/* 选项1: Zelle */}
                            <div className="border-l-4 border-blue-500 pl-4 py-2">
                                <h3 className="font-medium text-lg mb-2">{t('registrationSuccess.option1')}</h3>
                                <p className="text-gray-700 mb-2">
                                    {t('registrationSuccess.zelleInfo').split('{email}').map((part, i, array) =>
                                        i < array.length - 1 ?
                                            <React.Fragment key={i}>
                                                {part}<span className="text-blue-600">tuition@great-lakes-academy.org</span>
                                            </React.Fragment> :
                                            part
                                    )}
                                </p>
                                <ul className="list-disc list-inside text-gray-700 text-sm space-y-1 ml-2">
                                    <li>{t('registrationSuccess.zelleNote1').replace('{registrationId}', registrationData?.registrationId || 'xx')}</li>
                                    <li>{t('registrationSuccess.zelleNote2')}</li>
                                    <li>{t('registrationSuccess.zelleNote3')} (<span className="text-blue-600">info@great-lakes-academy.org</span>)</li>
                                </ul>
                            </div>

                            {/* 选项2: 邮寄支票 */}
                            <div className="border-l-4 border-blue-500 pl-4 py-2">
                                <h3 className="font-medium text-lg mb-2">{t('registrationSuccess.option2')}</h3>
                                <p className="text-gray-700 mb-2">
                                    {t('registrationSuccess.checkInfo')}
                                </p>
                                <div className="bg-gray-50 p-3 rounded mb-2">
                                    <p className="font-medium">Great Lakes Academy</p>
                                    <p>3300 Plymouth Blvd</p>
                                    <p># 46253</p>
                                    <p>Plymouth，MN 55447</p>
                                </div>
                                <p className="text-sm font-medium text-gray-700">
                                    {t('registrationSuccess.checkNote').replace('{registrationId}', registrationData?.registrationId || 'xx')}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="bg-blue-50 rounded-lg p-6 mb-8">
                    <h2 className="text-xl font-semibold mb-4">{t('registrationSuccess.nextSteps')}</h2>
                    <ul className="space-y-3">
                        <li className="flex">
                            <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-blue-600" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <p className="ml-3 text-gray-700">{t('registrationSuccess.step1')}</p>
                        </li>
                        <li className="flex">
                            <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-blue-600" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <p className="ml-3 text-gray-700">{t('registrationSuccess.step2')}</p>
                        </li>
                        <li className="flex">
                            <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-blue-600" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <p className="ml-3 text-gray-700">{t('registrationSuccess.step3')}</p>
                        </li>
                    </ul>
                </div>

                <div className="flex justify-center space-x-4">
                    <Link
                        to="/profile"
                        className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-6 rounded-md transition-colors"
                    >
                        {t('registrationSuccess.viewProfile')}
                    </Link>

                    <Link
                        to="/programs"
                        className="bg-gray-200 hover:bg-gray-300 text-gray-800 py-2 px-6 rounded-md transition-colors"
                    >
                        {t('registrationSuccess.browsePrograms')}
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default RegistrationSuccess;
